import React, { useState } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Building2, Plus } from 'lucide-react';

const inputCls = 'px-3 py-2 text-xs rounded-sm border w-full focus:outline-none focus:ring-1 focus:ring-[#1A4C7C]';
const inputStyle = { borderColor: 'rgba(136,150,168,0.3)', color: '#0D1B2A' };

export const CreateBusinessCard = ({ prefillName = '', onCreated }) => {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    legal_name: prefillName, trading_name: '', abn: '', entity_type: 'Company',
    industry_code: '', street_address: '', suburb: '', state: '', postcode: '',
    country: 'AU', employee_count: '', annual_turnover: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/entities', {
        ...form,
        employee_count: form.employee_count === '' ? null : parseInt(form.employee_count),
        annual_turnover: form.annual_turnover === '' ? null : parseFloat(form.annual_turnover),
        abn: form.abn || null,
      });
      onCreated(data);
    } catch (err) { setError(err?.response?.data?.detail || 'Failed to create business'); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button onClick={() => { setOpen(true); if (prefillName) set('legal_name', prefillName); }} data-testid="create-business-btn"
        className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold transition-colors hover:bg-[rgba(26,76,124,0.08)]"
        style={{ color: '#1A4C7C', border: '1px dashed rgba(26,76,124,0.4)' }}>
        <Plus className="w-3.5 h-3.5" /> Can't find the business? Create it
      </button>
    );
  }

  return (
    <div className="rounded-sm border bg-white p-4" style={{ borderColor: 'rgba(26,76,124,0.25)' }} data-testid="create-business-form">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4" style={{ color: '#1A4C7C' }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Create New Business</h3>
        <span className="text-[10px]" style={{ color: '#5C6B7A' }}>added to your register — a simulated ABN is issued if none is supplied</span>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input className={`${inputCls} md:col-span-2`} style={inputStyle} placeholder="Legal name *" required value={form.legal_name}
          onChange={e => set('legal_name', e.target.value)} data-testid="new-business-name-input" />
        <input className={inputCls} style={inputStyle} placeholder="Trading name (optional)" value={form.trading_name}
          onChange={e => set('trading_name', e.target.value)} data-testid="new-business-trading-input" />
        <input className={inputCls} style={inputStyle} placeholder={form.country === 'NZ' ? 'NZBN (13 digits, optional)' : 'ABN (11 digits, optional)'} value={form.abn}
          onChange={e => set('abn', e.target.value)} data-testid="new-business-abn-input" />
        <select className={inputCls} style={inputStyle} value={form.entity_type} onChange={e => set('entity_type', e.target.value)} data-testid="new-business-type-select">
          {['Company', 'Sole Trader', 'Partnership', 'Trust', 'Not-for-Profit'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className={inputCls} style={inputStyle} placeholder="Industry (e.g. Hospitality)" value={form.industry_code}
          onChange={e => set('industry_code', e.target.value)} data-testid="new-business-industry-input" />
        <input className={inputCls} style={inputStyle} placeholder="Street address" value={form.street_address}
          onChange={e => set('street_address', e.target.value)} data-testid="new-business-street-input" />
        <input className={inputCls} style={inputStyle} placeholder="Suburb / City" value={form.suburb}
          onChange={e => set('suburb', e.target.value)} data-testid="new-business-suburb-input" />
        <div className="flex gap-2">
          <input className={inputCls} style={inputStyle} placeholder="State" value={form.state}
            onChange={e => set('state', e.target.value)} data-testid="new-business-state-input" />
          <input className={inputCls} style={inputStyle} placeholder="Postcode" value={form.postcode}
            onChange={e => set('postcode', e.target.value)} data-testid="new-business-postcode-input" />
        </div>
        <select className={inputCls} style={inputStyle} value={form.country} onChange={e => set('country', e.target.value)} data-testid="new-business-country-select">
          <option value="AU">Australia</option><option value="NZ">New Zealand</option>
        </select>
        <input type="number" min="0" className={inputCls} style={inputStyle} placeholder="Employees (optional)" value={form.employee_count}
          onChange={e => set('employee_count', e.target.value)} data-testid="new-business-employees-input" />
        <input type="number" min="0" className={inputCls} style={inputStyle} placeholder="Annual turnover $ (optional)" value={form.annual_turnover}
          onChange={e => set('annual_turnover', e.target.value)} data-testid="new-business-turnover-input" />
      </form>
      {error && <p className="text-xs mt-2" style={{ color: '#E24B4A' }} data-testid="create-business-error">{error}</p>}
      <div className="flex gap-2 mt-3">
        <button onClick={submit} disabled={busy || form.legal_name.trim().length < 3} data-testid="create-business-submit"
          className="px-4 py-2 rounded-sm text-xs font-semibold text-white disabled:opacity-40" style={{ background: '#1A4C7C' }}>
          {busy ? 'Creating…' : 'Create & Start Submission'}
        </button>
        <button onClick={() => { setOpen(false); setError(''); }} className="px-3 py-2 rounded-sm text-xs font-medium" style={{ color: '#5C6B7A' }}>Cancel</button>
      </div>
    </div>
  );
};

export default CreateBusinessCard;
